/**
 * Why an agreement was dismissed, or why a rebid was asked for.
 *
 * ── Why these two statuses and no others ────────────────────────────────────
 * Stacie, on where a reason is worth asking for:
 *
 *   "I would only care if it's going to rebid requested so I can tell the hotel
 *    why we're requesting a rebid. or if anyone changes it to dismissed.
 *    Because those, again, will need a reason for dismissal."
 *   "The other ones I don't think need notes… if it's sent, if it's pending
 *    acceptance, if it's accepted, if it's created, I don't want to over notify
 *    the hoteliers."
 *
 * That is a hard requirement rather than a preference — over-prompting is the
 * specific thing that killed the predecessor tool ("that's what they hated about
 * BTP was there was a notification for everything… You get 75 emails").
 *
 * ── A SUGGESTED list, not a closed one ─────────────────────────────────────
 * The plan asks for a short closed list, and closing it is the right end state.
 * It is not closed YET because the vocabulary is Stacie's and only one entry has
 * actually been confirmed — "not worth pursuing", approved by Jeff on
 * 2026-09-14 out of her initial-conversation branch ("if not, it would move to
 * dismissed status"). The rest below are drawn from her own words in the
 * feedback log rather than invented, and `other` keeps the free-text path open
 * so a real reason is never refused by a picklist that is still provisional.
 *
 * Closing the list is a one-line change here once she has reviewed it.
 */

export interface StatusReason {
  value: string;
  label: string;
}

/**
 * Dismissal reasons.
 *
 * `not_worth_pursuing` is the confirmed one. The other two trace to her
 * description of when a dismissal is logged: "if we dismissed it because it was
 * a crap offer or a competitor's rates were better, we can log that."
 */
export const DISMISSAL_REASONS: readonly StatusReason[] = [
  { value: 'not_worth_pursuing', label: 'Not worth pursuing' },
  { value: 'rate_not_competitive', label: 'Rate not competitive' },
  { value: 'competitor_rate_better', label: 'A competitor’s rate was better' },
  { value: 'hotel_declined', label: 'The hotel declined to bid' },
  { value: 'other', label: 'Other — explain below' },
];

/** Rebid reasons. The point of the prompt is that it is told to the hotel:
 *  "so I can tell the hotel why we're requesting a rebid." */
export const REBID_REASONS: readonly StatusReason[] = [
  { value: 'rate_too_high', label: 'Rate is too high' },
  { value: 'terms_unacceptable', label: 'Terms are unacceptable' },
  { value: 'competitor_rate_better', label: 'A competitor’s rate was better' },
  { value: 'other', label: 'Other — explain below' },
];

/** The suggested reasons for a target status, or null when it takes none. */
export function reasonsFor(status: string): readonly StatusReason[] | null {
  if (status === 'dismissed') return DISMISSAL_REASONS;
  if (status === 'rebid_requested') return REBID_REASONS;
  return null;
}

/**
 * Does moving to this status ask why?
 *
 * The CLOSURES keep their prompt — they are irreversible, and removing a prompt
 * from an irreversible action is not something to infer. `rebid_requested` is
 * ADDED: it is not a closure, but Stacie named it as the other case that needs
 * a reason, because the reason is what she tells the hotel.
 */
export function promptsForReason(
  status: string,
  closesTheAgreement: (s: string) => boolean,
): boolean {
  return status === 'rebid_requested' || closesTheAgreement(status);
}

/** The stored `reason` string for a chosen option plus any free text. Keeps the
 *  label, not the key: the audit trail is read by people, and `other` on its own
 *  says nothing. */
export function composeReason(option: string, freeText: string): string | null {
  const text = freeText.trim();
  const chosen = [...DISMISSAL_REASONS, ...REBID_REASONS].find(
    (r) => r.value === option,
  );
  if (!chosen || chosen.value === 'other') return text || null;
  return text ? `${chosen.label} — ${text}` : chosen.label;
}
